import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	MenuItem,
	FormControlLabel,
	Switch,
	makeStyles,
	CircularProgress,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles((theme) => ({
	row: {
		display: "flex",
		gap: 8,
		marginTop: 8,
	},
	full: { width: "100%" },
	palette: {
		display: "flex",
		flexWrap: "wrap",
		gap: 6,
		marginTop: 4,
	},
	swatch: {
		width: 26,
		height: 26,
		borderRadius: "50%",
		cursor: "pointer",
		border: "2px solid transparent",
		boxSizing: "border-box",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 14,
		color: "#fff",
		fontWeight: 700,
	},
	swatchSelected: {
		border: `2px solid ${theme.palette.text.primary}`,
		boxShadow: "0 0 0 2px #fff inset",
	},
	swatchEmpty: {
		background:
			"repeating-linear-gradient(45deg,#bbb,#bbb 4px,#fff 4px,#fff 8px)",
		color: "#555",
	},
}));

const KINDS = [
	{ value: "meeting", label: "Reunião" },
	{ value: "call", label: "Ligação" },
	{ value: "task", label: "Tarefa" },
	{ value: "reminder", label: "Lembrete" },
];

const STATUSES = [
	{ value: "scheduled", label: "Agendado" },
	{ value: "done", label: "Concluído" },
	{ value: "canceled", label: "Cancelado" },
];

const COLOR_PALETTE = [
	{ value: "", label: "Padrão (por tipo)" },
	{ value: "#1976d2", label: "Azul" },
	{ value: "#0288d1", label: "Azul claro" },
	{ value: "#388e3c", label: "Verde" },
	{ value: "#43a047", label: "Verde claro" },
	{ value: "#f57c00", label: "Laranja" },
	{ value: "#fbc02d", label: "Amarelo" },
	{ value: "#e53935", label: "Vermelho" },
	{ value: "#d81b60", label: "Rosa" },
	{ value: "#7b1fa2", label: "Roxo" },
	{ value: "#5e35b1", label: "Violeta" },
	{ value: "#00897b", label: "Teal" },
	{ value: "#6d4c41", label: "Marrom" },
	{ value: "#455a64", label: "Cinza" },
	{ value: "#212121", label: "Preto" },
];

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (date) => {
	const d = date instanceof Date ? date : new Date(date);
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
		d.getHours()
	)}:${pad(d.getMinutes())}`;
};

const AppointmentModal = ({
	open,
	onClose,
	appointment,
	defaultDate,
	defaultKind,
	defaultTitle,
	defaultTicketId,
	defaultClientId,
	onSaved,
	onDeleted,
}) => {
	const classes = useStyles();
	const [confirm, confirmDialog] = useConfirm();
	const isEdit = !!appointment;

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [location, setLocation] = useState("");
	const [kind, setKind] = useState("meeting");
	const [status, setStatus] = useState("scheduled");
	const [startAt, setStartAt] = useState("");
	const [endAt, setEndAt] = useState("");
	const [allDay, setAllDay] = useState(false);
	const [reminderMinutes, setReminderMinutes] = useState(10);
	const [color, setColor] = useState("");
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		if (!open) return;
		if (isEdit) {
			setTitle(appointment.title || "");
			setDescription(appointment.description || "");
			setLocation(appointment.location || "");
			setKind(appointment.kind || "meeting");
			setStatus(appointment.status || "scheduled");
			setStartAt(
				appointment.startAt ? toLocal(appointment.startAt) : ""
			);
			setEndAt(appointment.endAt ? toLocal(appointment.endAt) : "");
			setAllDay(!!appointment.allDay);
			setReminderMinutes(
				appointment.reminderMinutes === undefined ? 10 : appointment.reminderMinutes
			);
			setColor(appointment.color || "");
		} else {
			const base =
				defaultDate instanceof Date && !Number.isNaN(defaultDate)
					? new Date(defaultDate)
					: new Date();
			base.setHours(9, 0, 0, 0);
			const end = new Date(base);
			end.setHours(end.getHours() + 1);
			setTitle(defaultTitle || "");
			setDescription("");
			setLocation("");
			setKind(defaultKind || "meeting");
			setStatus("scheduled");
			setStartAt(toLocal(base));
			setEndAt(toLocal(end));
			setAllDay(false);
			setReminderMinutes(10);
			setColor("");
		}
	}, [open, appointment, defaultDate, defaultKind, defaultTitle, isEdit]);

	const handleSave = async () => {
		if (!title.trim()) {
			toast.warn("Informe um título");
			return;
		}
		if (!startAt) {
			toast.warn("Informe a data/hora de início");
			return;
		}
		const startISO = new Date(startAt).toISOString();
		const endISO = endAt ? new Date(endAt).toISOString() : null;
		if (endISO && new Date(endISO) < new Date(startISO)) {
			toast.warn("Fim deve ser após o início");
			return;
		}
		const payload = {
			title,
			description,
			location,
			kind,
			status,
			startAt: startISO,
			endAt: endISO,
			allDay,
			reminderMinutes: Number(reminderMinutes),
			color: color || null,
		};
		setSaving(true);
		try {
			if (isEdit) {
				const { data } = await api.put(
					`/appointments/${appointment.id}`,
					payload
				);
				toast.success("Compromisso atualizado");
				onSaved && onSaved(data);
			} else {
				const createPayload = { ...payload };
				if (defaultTicketId) createPayload.ticketId = defaultTicketId;
				if (defaultClientId) createPayload.clientId = defaultClientId;
				const { data } = await api.post("/appointments", createPayload);
				toast.success("Compromisso criado");
				onSaved && onSaved(data);
			}
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!isEdit) return;
		if (!(await confirm({ title: "Excluir este compromisso?", message: "Ele some da agenda de todos os envolvidos." }))) return;
		setDeleting(true);
		try {
			await api.delete(`/appointments/${appointment.id}`);
			toast.success("Compromisso excluído");
			onDeleted && onDeleted();
		} catch (err) {
			toastError(err);
		} finally {
			setDeleting(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			{confirmDialog}
			<DialogTitle>
				{isEdit ? "Editar compromisso" : "Novo compromisso"}
			</DialogTitle>
			<DialogContent>
				<TextField
					className={classes.full}
					label="Título"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					variant="outlined"
				/>
				<div className={classes.row}>
					<TextField
						select
						label="Tipo"
						value={kind}
						onChange={(e) => setKind(e.target.value)}
						variant="outlined"
						style={{ flex: 1 }}
					>
						{KINDS.map((k) => (
							<MenuItem key={k.value} value={k.value}>
								{k.label}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						label="Status"
						value={status}
						onChange={(e) => setStatus(e.target.value)}
						variant="outlined"
						style={{ flex: 1 }}
					>
						{STATUSES.map((s) => (
							<MenuItem key={s.value} value={s.value}>
								{s.label}
							</MenuItem>
						))}
					</TextField>
				</div>
				<div className={classes.row}>
					<TextField
						label="Início"
						type="datetime-local"
						value={startAt}
						onChange={(e) => setStartAt(e.target.value)}
						InputLabelProps={{ shrink: true }}
						variant="outlined"
						style={{ flex: 1 }}
					/>
					<TextField
						label="Fim"
						type="datetime-local"
						value={endAt}
						onChange={(e) => setEndAt(e.target.value)}
						InputLabelProps={{ shrink: true }}
						variant="outlined"
						style={{ flex: 1 }}
					/>
				</div>
				<div className={classes.row}>
					<FormControlLabel
						control={
							<Switch
								checked={allDay}
								onChange={(e) => setAllDay(e.target.checked)}
								color="primary"
							/>
						}
						label="Dia inteiro"
					/>
					<TextField
						label="Cor (hex)"
						value={color}
						onChange={(e) => setColor(e.target.value)}
						placeholder="#1976d2"
						variant="outlined"
						style={{ flex: 1 }}
					/>
					{/* Compromisso de dia inteiro não avisa por minutos —
					    "faltam 10 minutos" não quer dizer nada para algo que
					    dura o dia. */}
					<TextField
						select
						label="Avisar antes"
						value={reminderMinutes}
						onChange={(e) => setReminderMinutes(e.target.value)}
						variant="outlined"
						disabled={allDay}
						helperText={allDay ? "Não se aplica a dia inteiro" : " "}
						style={{ flex: 1 }}
					>
						<MenuItem value={0}>Não avisar</MenuItem>
						<MenuItem value={5}>5 minutos antes</MenuItem>
						<MenuItem value={10}>10 minutos antes</MenuItem>
						<MenuItem value={15}>15 minutos antes</MenuItem>
						<MenuItem value={30}>30 minutos antes</MenuItem>
						<MenuItem value={60}>1 hora antes</MenuItem>
						<MenuItem value={120}>2 horas antes</MenuItem>
						<MenuItem value={1440}>1 dia antes</MenuItem>
					</TextField>
				</div>
				<div className={classes.palette}>
					{COLOR_PALETTE.map((c) => {
						const selected =
							(c.value || "").toLowerCase() ===
							(color || "").toLowerCase();
						return (
							<span
								key={c.value || "default"}
								title={c.label}
								onClick={() => setColor(c.value)}
								className={[
									classes.swatch,
									selected ? classes.swatchSelected : "",
									!c.value ? classes.swatchEmpty : "",
								].join(" ")}
								style={
									c.value ? { background: c.value } : undefined
								}
							>
								{selected ? "✓" : ""}
							</span>
						);
					})}
				</div>
				<TextField
					className={classes.full}
					label="Local"
					value={location}
					onChange={(e) => setLocation(e.target.value)}
					variant="outlined"
					style={{ marginTop: 8 }}
				/>
				<TextField
					className={classes.full}
					label="Descrição"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					multiline
					minRows={2}
					maxRows={6}
					variant="outlined"
					style={{ marginTop: 8 }}
				/>
			</DialogContent>
			<DialogActions>
				{isEdit && (
					<Button
						onClick={handleDelete}
						color="secondary"
						startIcon={<DeleteOutlineIcon />}
						disabled={deleting || saving}
					>
						Excluir
					</Button>
				)}
				<div style={{ flex: 1 }} />
				<Button onClick={onClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					onClick={handleSave}
					color="primary"
					variant="contained"
					disabled={saving}
					startIcon={
						saving ? (
							<CircularProgress size={16} color="inherit" />
						) : null
					}
				>
					{isEdit ? "Salvar" : "Criar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AppointmentModal;
